import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogIn } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import BrandLogo from "@/components/BrandLogo";

export default function CustomerLogin() {
  const { setCustomerAuth, customerToken } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/account";
  const [identifier, setIdentifier] = useState(location.state?.prefill || "");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (customerToken) navigate(from, { replace: true });
  }, [customerToken, navigate, from]);

  const submit = async (e) => {
    e.preventDefault();
    if (!identifier.trim() || !password) {
      toast.error("Please enter your email or mobile and password");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/customer-login", {
        identifier: identifier.trim(),
        password,
      });
      setCustomerAuth(data.access_token, data.customer);
      toast.success(
        `Welcome back${data.customer?.name ? ", " + data.customer.name : ""}`
      );
      navigate(from);
    } catch (err) {
      toast.error(errMsg(err, "Login failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen place-items-center bg-muted/40 p-6">
      <div className="w-full max-w-md rounded-2xl border border-border/70 bg-card p-8 shadow-xl">
        <BrandLogo />
        <h1 className="mt-6 font-serif-display text-3xl font-semibold text-secondary">
          Sign in
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Track your Tezz Khana quotes and bookings.
        </p>

        <form
          onSubmit={submit}
          className="mt-8 space-y-4"
          data-testid="customer-login-form"
        >
          <label className="block">
            <span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
              Email or Mobile
            </span>
            <input
              data-testid="customer-login-identifier"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              required
              autoComplete="username"
              className="w-full rounded-xl border border-border/70 bg-background px-4 py-3 text-sm focus:border-primary focus:outline-none"
            />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
              Password
            </span>
            <input
              type="password"
              data-testid="customer-login-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              className="w-full rounded-xl border border-border/70 bg-background px-4 py-3 text-sm focus:border-primary focus:outline-none"
            />
          </label>
          <button
            type="submit"
            disabled={loading}
            data-testid="customer-login-submit"
            className="btn-primary w-full"
          >
            <LogIn className="h-4 w-4" />
            {loading ? "Signing in…" : "Sign in"}
          </button>
          <div className="text-center">
            <Link to="/account/forgot-password" data-testid="customer-forgot-link" className="text-xs font-semibold text-primary hover:underline">
              Forgot password?
            </Link>
          </div>
        </form>

        <p className="mt-6 text-center text-sm text-muted-foreground">
          New to Tezz Khana?{" "}
          <Link
            to="/account/register"
            state={location.state}
            data-testid="customer-login-register-link"
            className="font-semibold text-primary hover:underline"
          >
            Create an account
          </Link>
        </p>
      </div>
    </div>
  );
}
